import React, { useEffect, useContext, useState, useRef } from "react";

import axios from "axios";
import { BASE_URL } from "../../../../BaseUrl";

import {
  _t,
  getCookie,
  customStyle,
} from "../../../../functions/Functions";
import { useTranslation } from "react-i18next";

import { Helmet } from "react-helmet";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import Moment from "react-moment";
import moment from "moment";
import Select from "react-select";
import ReactHTMLTableToExcel from "react-html-table-to-excel";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { useReactToPrint } from "react-to-print";

import ReportSidebar from "./ReportSidebar";

import { SettingsContext } from "../../../../contexts/Settings";
import { RestaurantContext } from "../../../../contexts/Restaurant";
import { UserContext } from "../../../../contexts/User";
import CustomSpinLoader from "../../../components/CustomSpinLoader";
import ReportTypeOptions from "../../../components/Reports/ReportTypeOptions";
import ReportExcelPrintGap from "../../../components/Reports/ReportExcelPrintGap";

const BankDropReport = () => {
  const { t } = useTranslation();

  const {
    loading,
    setLoading,
    dataPaginating,
    setDataPaginating,
    generalSettings,
  } = useContext(SettingsContext);
  const { branchForSearch } = useContext(RestaurantContext);
  const { authUserInfo } = useContext(UserContext);

  const [reportData, setReportData] = useState(null);
  const componentRef = useRef();
  const componentRefThermal = useRef();
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [branch, setBranch] = useState(null);
  const [generatedReport, setGeneratedReport] = useState(false);
  const [option, setOption] = useState("excel");

  useEffect(() => {
    setLoading(true);
    setTimeout(() => setLoading(false), 500);
  }, [generalSettings]);

  const [branchSearch, setBranchSearch] = useState(null);
  useEffect(() => {
    if (authUserInfo?.details?.user_type === "staff" || authUserInfo?.details?.user_type === "manager") {
      setBranchSearch(
        branchForSearch?.filter(
          (b) => b.id === authUserInfo.details.branch_id
        ) ?? []
      );
    } else {
      setBranchSearch(branchForSearch ?? []);
    }
  }, [authUserInfo, branchForSearch]);

  const common_toast = {
    position: "bottom-center",
    closeButton: false,
    autoClose: 10000,
    hideProgressBar: false,
    closeOnClick: true,
    pauseOnHover: true,
    className: "text-center toast-notification",
  };

  const handleBranch = (b) => setBranch(b);
  const handleOption = (e) => setOption(e.target.value);

  const thermalPrint = useReactToPrint({
    content: () => componentRefThermal.current,
    pageStyle: `@page { margin: 15px 7px; }`,
  });

  const getReport = () => {
    setReportData(null);
    setGeneratedReport(false);

    if (
      (authUserInfo?.details?.user_type === "staff" || authUserInfo?.details?.user_type === "manager") &&
      !branch?.id
    ) {
      toast.error(`${_t(t("Please select Branch to generate report"))}`, common_toast);
      return;
    }
    if (!startDate || !endDate) {
      toast.error(`${_t(t("Please select from date and to date"))}`, common_toast);
      return;
    }

    setDataPaginating(true);
    const from_date = moment(startDate).format("YYYY-MM-DD");
    const to_date = moment(endDate).format("YYYY-MM-DD");
    const url = BASE_URL + "/settings/bank-drop-report";
    const formData = new FormData();
    formData.append("branch_id", branch?.id ?? "");
    formData.append("from_date", from_date);
    formData.append("to_date", to_date);

    axios
      .post(url, formData, {
        headers: { Authorization: `Bearer ${getCookie()}` },
      })
      .then((res) => {
        const raw = res.data;
        let data;
        if (Array.isArray(raw)) {
          data = raw;
        } else if (raw && typeof raw === "object" && !Array.isArray(raw)) {
          // API returns { "Branch Name": [ {...}, ... ] }
          data = raw;
        } else {
          data = raw?.data ?? [];
        }
        setReportData(data);
        setGeneratedReport(true);
        setDataPaginating(false);
        if (option === "excel") {
          setTimeout(() => componentRef.current?.handleDownload?.(), 100);
        } else {
          setTimeout(() => thermalPrint(), 100);
        }
      })
      .catch((err) => {
        setDataPaginating(false);
        const msg = err?.response?.data?.message || t("Please try again");
        toast.error(`${_t(t(msg))}`, common_toast);
      });
  };

  const header = [
    { name: _t(t("Group")), render: (item, index) => null },
    { name: _t(t("Document No")), key: "document_identity" },
    {
      name: _t(t("Document Date")),
      render: (item) =>
        item.document_date ? (
          <Moment format="LL">{item.document_date}</Moment>
        ) : (
          "-"
        ),
    },
    { name: _t(t("Branch")), key: "branch_name" },
    {
      name: _t(t("Bank Amount")),
      key: "bank_amount",
      total: 1,
    },
    {
      name: _t(t("Expense Amount")),
      key: "expense_amount",
      total: 1,
    },
    {
      name: _t(t("Total Amount")),
      key: "trapped_amount",
      total: 1,
    },
    {
      name: _t(t("Description")),
      key: "remarks",
      render: (item) => item.remarks ?? "-",
    },
    { name: _t(t("Status")), key: "is_post" },
  ];

  return (
    <>
      <Helmet>
        <title>{_t(t("Bank Drop Report"))}</title>
      </Helmet>

      <ReactHTMLTableToExcel
        id="test-table-xls-button"
        className="download-table-xls-button btn btn-success float-right mr-3 invisible"
        table="table-to-xls"
        filename="Bank Drop Report"
        sheet="sheet 1"
        buttonText="Bank Drop Report"
        ref={componentRef}
      />

      <main id="main" data-simplebar>
        <div className="container-fluid">
          <div className="row t-mt-10 gx-2">
            <div className="col-lg-2 col-xxl-2 t-mb-30 mb-lg-0">
              <ReportSidebar />
            </div>

            <div className="col-lg-10 col-xxl-10 t-mb-30 mb-md-0">
              <div className="t-bg-white">
                <div
                  className="fk-scroll--pos-menu table-bottom-info-hide"
                  data-simplebar
                >
                  <div className="t-pl-15 t-pr-15">
                    <CustomSpinLoader loading={loading || dataPaginating} />

                    <div key="bank-drop-report-form">
                      <div className="gx-2 align-items-center t-pt-15 t-pb-15">
                        <div className="row t-mb-15 mb-md-0">
                          <ul className="t-list fk-breadcrumb col-6">
                            <li className="fk-breadcrumb__list">
                              <span className="pl-3 t-link fk-breadcrumb__link text-capitalize">
                                {_t(t("Bank Drop Report"))}
                              </span>
                            </li>
                          </ul>
                          <div className="col-6 d-flex justify-content-end">
                            <ReportTypeOptions
                              selectedOption={option}
                              onChange={handleOption}
                            />
                          </div>
                        </div>
                      </div>

                      <div className="row mt-2">
                        <div className="col-md-2 d-md-block">
                          <DatePicker
                            selected={startDate}
                            onChange={(date) => setStartDate(date)}
                            peekNextMonth
                            showMonthDropdown
                            showYearDropdown
                            dropdownMode="select"
                            className="form-control sm-text py-2 t-mb-15 mb-md-0"
                            placeholderText={_t(t("From date"))}
                            shouldCloseOnSelect={true}
                          />
                        </div>
                        <div className="col-md-2 t-mb-15 mb-md-0">
                          <DatePicker
                            selected={endDate}
                            onChange={(date) => setEndDate(date)}
                            peekNextMonth
                            showMonthDropdown
                            showYearDropdown
                            dropdownMode="select"
                            className="form-control sm-text py-2"
                            placeholderText={_t(t("To date"))}
                            shouldCloseOnSelect={true}
                          />
                        </div>
                        <div className="col-md-4 t-mb-15 mb-md-0">
                          <Select
                            options={branchSearch ?? []}
                            getOptionLabel={(opt) => opt.name}
                            getOptionValue={(opt) => opt.id}
                            value={branch}
                            isClearable
                            classNamePrefix="select"
                            className="sm-text"
                            onChange={handleBranch}
                            styles={customStyle}
                            maxMenuHeight="200px"
                            placeholder={_t(t("Select a branch")) + ".."}
                          />
                        </div>
                        <div className="col-2"></div>
                        <div className="col-2">
                          <button
                            className="float-right btn btn-block btn-primary text-uppercase sm-text py-2 rounded"
                            onClick={getReport}
                            disabled={dataPaginating}
                          >
                            {_t(t("Generate Report"))}
                          </button>
                        </div>
                      </div>

                      <ReportExcelPrintGap
                        header={header}
                        generatedReport={generatedReport}
                        heading={_t(t("Bank Drop Report"))}
                        reportData={reportData}
                        startDate={startDate}
                        endDate={endDate}
                        componentRef={componentRef}
                        printRef={componentRefThermal}
                        branch={{
                          name: branch?.name ?? _t(t("All Branch")),
                          show: true,
                        }}
                        type={option}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>
    </>
  );
};

export default BankDropReport;
